import { useMemo } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

import { useSKUsTableRows } from "../../../../pages/system-settings/tabs/skus/hooks/useSKUsTableRows";

interface SKU {
  id: string;
  skuNumber: string;
  configurationName: string;
  category: string;
  skuDataConfigurations: string;
  nodeCount: number;
  defaultIp?: string | null;
}

interface ISKUSelect {
  skuId?: string | null;
  label?: string;
  onSKUChange: (sku: SKU) => void;
  error?: boolean;
  helperText?: string;
}

interface ISKUOption {
  title: string;
  value: SKU;
}

interface SKURow {
  id?: string;
  skuNumber?: string;
  configurationName?: string;
  category?: string | null;
  skuDataConfigurations?: string;
  nodeCount?: number;
  defaultIp?: string | null;
}

const SKUSelect = ({
  skuId,
  label,
  onSKUChange,
  error = false,
  helperText = "",
}: ISKUSelect): JSX.Element => {
  const { rows, loading } = useSKUsTableRows();

  const options = useMemo((): ISKUOption[] => {
    return (
      rows
        ?.map((item: SKURow): ISKUOption => {
          // Display format: "SKU Number - Configuration Name (Category)"
          const categoryPart = item.category ? ` (${item.category})` : "";
          const displayTitle = `${item.skuNumber} - ${item.configurationName}${categoryPart}`;

          return {
            title: displayTitle,
            value: {
              id: item.id ?? "",
              skuNumber: item.skuNumber ?? "",
              configurationName: item.configurationName ?? "",
              category: item.category ?? "",
              skuDataConfigurations: item.skuDataConfigurations ?? "",
              nodeCount: item.nodeCount ?? 1,
              defaultIp: item.defaultIp ?? null,
            },
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title)) ?? []
    );
  }, [rows]);

  const memoizedValue = useMemo((): ISKUOption | null => {
    if (!skuId) {
      return null;
    }

    return options.find(option => option.value.id === skuId) ?? null;
  }, [skuId, options]);

  return (
    <Autocomplete
      fullWidth
      disableClearable
      loading={loading}
      value={memoizedValue as ISKUOption}
      onChange={(_event, newValue: ISKUOption | null): void => {
        if (newValue) {
          onSKUChange(newValue.value);
        }
      }}
      getOptionLabel={(option: ISKUOption): string => option?.title ?? ""}
      isOptionEqualToValue={(option, optionValue): boolean => {
        return option?.value?.id === optionValue?.value?.id;
      }}
      options={options}
      renderInput={(params): JSX.Element => (
        <TextField
          {...params}
          label={label ?? "SKU Configuration"}
          error={error}
          helperText={error ? helperText : ""}
          inputProps={{
            ...params.inputProps,
            readOnly: true,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SKUSelect;
